import { watchEffect, type Directive, type DirectiveBinding } from 'vue';
import { usePermissionStore } from '@/stores/permission';

/**
 * v-permission 指令
 *
 * 根据当前用户权限控制元素的显隐或禁用状态。
 *
 * 用法：
 * - `v-permission="'lead:edit'"` → 无权限时隐藏元素
 * - `v-permission.disabled="'lead:edit'"` → 无权限时禁用元素 + 降低透明度
 * - `v-permission="['lead:edit', 'lead:create']"` → 拥有任一权限即可（any 语义）
 *
 * @see requirements.md - Requirement 12.2, 12.3
 */

/** 检查用户是否拥有所需权限 */
function checkPermission(binding: DirectiveBinding): boolean {
  const permissionStore = usePermissionStore();
  const value = binding.value;

  if (!value) return true;

  if (Array.isArray(value)) {
    return permissionStore.hasAnyPermission(value);
  }

  return permissionStore.hasPermission(value);
}

export const vPermission: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    originalDisplayMap.set(el, el.style.display);
    bindingMap.set(el, binding);
    stopMap.set(
      el,
      watchEffect(() => applyPermission(el, bindingMap.get(el) ?? binding)),
    );
  },

  updated(el: HTMLElement, binding: DirectiveBinding) {
    bindingMap.set(el, binding);
    applyPermission(el, binding);
  },

  unmounted(el: HTMLElement) {
    stopMap.get(el)?.();
    stopMap.delete(el);
    bindingMap.delete(el);
    originalDisplayMap.delete(el);
  },
};

const bindingMap = new WeakMap<HTMLElement, DirectiveBinding>();
const originalDisplayMap = new WeakMap<HTMLElement, string>();
const stopMap = new WeakMap<HTMLElement, () => void>();

function applyPermission(el: HTMLElement, binding: DirectiveBinding): void {
  const hasAccess = checkPermission(binding);
  if (binding.modifiers.disabled) {
    if (hasAccess) {
      el.removeAttribute('disabled');
      el.style.opacity = '';
      el.style.pointerEvents = '';
      el.style.cursor = '';
    } else {
      el.setAttribute('disabled', 'true');
      el.style.opacity = '0.5';
      el.style.pointerEvents = 'none';
      el.style.cursor = 'not-allowed';
    }
    return;
  }

  el.style.display = hasAccess ? (originalDisplayMap.get(el) ?? '') : 'none';
}
